import React from 'react'
import PropTypes from 'prop-types'
import { Box, Typography, Divider } from '@mui/material'
import { CommonDialog, CommonButton } from '@/components/common'
import { East as ArrowRightIcon } from '@mui/icons-material'

const AppointmentDetail = ({ label, date, time, location, address }) => (
  <Box sx={{ flex: 1 }}>
    <Typography
      variant='caption'
      sx={{
        fontWeight: 700,
        textTransform: 'uppercase',
        letterSpacing: 0.8,
        color: 'text.secondary',
        display: 'block',
        mb: 1,
      }}
    >
      {label}
    </Typography>
    {date && (
      <Typography variant='body2' fontWeight={600} color='primary'>
        {date}
      </Typography>
    )}
    {time && (
      <Typography variant='body2' color='text.secondary'>
        {time}
      </Typography>
    )}
    {location && (
      <Typography variant='body2' fontWeight={500} sx={{ mt: 0.5 }}>
        {location}
      </Typography>
    )}
    {address && (
      <Typography variant='body2' color='text.secondary'>
        {address}
      </Typography>
    )}
  </Box>
)

export const RescheduleConfirmationDialog = ({
  open,
  onClose,
  onReschedule,
  onBookAsNew,
  loading = false,
  existingAppointment,
  newAppointment,
}) => {
  return (
    <CommonDialog
      open={open}
      onClose={onClose}
      maxWidth='sm'
      fullWidth
      title='You Are Currently Rescheduling'
      persistent={false}
      actions={
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 2,
            width: '100%',
            justifyContent: 'center',
          }}
        >
          <CommonButton
            id='reschedule-proceed-btn'
            label='Reschedule'
            variant='primary'
            color='primary'
            onClick={onReschedule}
            loading={loading}
            disabled={loading}
          />
          <CommonButton
            id='reschedule-book-new-btn'
            label='Book as New Appointment'
            variant='secondary'
            color='primary'
            onClick={onBookAsNew}
            disabled={loading}
          />
          <CommonButton
            id='reschedule-go-back-btn'
            label='Go Back'
            variant='text'
            color='primary'
            onClick={onClose}
            disabled={loading}
          />
        </Box>
      }
    >
      <Box py={1}>
        {(existingAppointment || newAppointment) && (
          <>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: { xs: 1, sm: 2 },
                mb: 2,
                flexDirection: { xs: 'column', sm: 'row' },
              }}
            >
              {existingAppointment && (
                <AppointmentDetail
                  label='Current Appointment'
                  {...existingAppointment}
                />
              )}
              {existingAppointment && newAppointment && (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    pt: { xs: 0, sm: 3 },
                    alignSelf: { xs: 'center', sm: 'flex-start' },
                  }}
                >
                  <ArrowRightIcon color='primary' />
                </Box>
              )}
              {newAppointment && (
                <AppointmentDetail
                  label='New Appointment'
                  {...newAppointment}
                />
              )}
            </Box>
            <Divider sx={{ mb: 2 }} />
          </>
        )}
        <Typography variant='body2' color='text.secondary' sx={{ mb: 0.5 }}>
          <strong>Reschedule</strong> — cancel your existing appointment and
          book this slot instead.
        </Typography>
        <Typography variant='body2' color='text.secondary'>
          <strong>Book as New Appointment</strong> — keep your existing
          appointment and book this slot separately.
        </Typography>
      </Box>
    </CommonDialog>
  )
}

RescheduleConfirmationDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onReschedule: PropTypes.func.isRequired,
  onBookAsNew: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  existingAppointment: PropTypes.shape({
    date: PropTypes.string,
    time: PropTypes.string,
    location: PropTypes.string,
    procedure_type: PropTypes.string,
    address: PropTypes.string,
  }),
  newAppointment: PropTypes.shape({
    date: PropTypes.string,
    time: PropTypes.string,
    location: PropTypes.string,
    address: PropTypes.string,
  }),
}
